import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Catches a render-time crash and says so.
 *
 * WHY THIS IS NOT OPTIONAL
 * ------------------------
 * Without a boundary, one thrown error anywhere in the tree unmounts the whole
 * application and React replaces it with nothing. The user sees a white page.
 * No message, no address, no indication that anything failed rather than
 * loaded slowly — and the only trace is a stack in a console they will not
 * open.
 *
 * That is precisely how the `can()` crash on `/staff` presented: a signed-in
 * registrar reached their dashboard and got a blank screen, and diagnosing it
 * took a service-by-service trace of a bug the page could have named itself.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 * --------------------------------
 * It does not retry, and it does not swallow. A component that threw once will
 * throw again on the same state, so an automatic retry produces a flicker
 * instead of a fix. Re-raising into the console is kept so the stack survives
 * for whoever is looking.
 *
 * It also does not catch async failures — a rejected fetch never passes through
 * here. Those are TanStack Query's, and the query error states handle them.
 * This is the last resort for the errors nothing else can see.
 */
interface ErrorBoundaryState {
    error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
    public state: ErrorBoundaryState = { error: null };

    /**
     * Records the error so the next render can show it.
     *
     * @param error - What was thrown
     * @returns The next state
     */
    public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    /**
     * Re-raises into the console so the component stack is not lost.
     *
     * @param error - What was thrown
     * @param info - React's component stack
     */
    public componentDidCatch(error: Error, info: ErrorInfo): void {
        console.error("Unhandled render error", error, info.componentStack);
    }

    public render(): ReactNode {
        const { error } = this.state;

        if (!error) {
            return this.props.children;
        }

        return (
            <div className="flex min-h-dvh items-center justify-center bg-base p-6">
                <div className="w-full max-w-lg rounded-xl border border-subtle bg-raised p-6">
                    <h1 className="text-lg font-semibold text-primary">
                        This page could not be displayed
                    </h1>
                    <p className="mt-2 text-sm text-secondary">
                        Something in the interface failed while rendering. Your
                        work is not affected — nothing was saved or changed.
                        Reloading usually clears it.
                    </p>

                    {/* The message, verbatim and not behind a toggle. A user
                        reporting "it went blank" is unactionable; a user
                        reporting "Cannot read properties of undefined (reading
                        'some')" is a one-line fix. */}
                    <pre className="mt-4 overflow-x-auto rounded-md bg-sunken p-3 text-xs text-secondary">
                        {error.message}
                    </pre>

                    <button
                        type="button"
                        onClick={() => window.location.reload()}
                        className="mt-4 rounded-md bg-accent px-3 py-2 text-sm font-medium text-white"
                    >
                        Reload the page
                    </button>
                </div>
            </div>
        );
    }
}
